/**
 * <product-component> — product page gallery wrapper.
 * Optional fullscreen zoom gallery via product-gallery.js.
 *
 * Fixed-info scrolling contract:
 * - `[data-fixed-info-container]` bounds the sticky column to the media height.
 * - `[data-fixed-info-spacer]` preserves the current position when scroll direction changes.
 * - `[data-fixed-info-content]` pins its bottom while scrolling down and its top while scrolling up.
 */

import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';

class ProductComponent extends HTMLElement {
  #initialized = false;
  #swiper = null;
  #delegate = null;
  #options = null;
  #headerScrollRafId = null;
  #lastScrollY = 0;
  #stickyHeaderWrap = null;
  #imageZoom = null;
  #zoomLoadListener = null;
  #fixedInfoContainer = null;
  #fixedInfoSpacer = null;
  #fixedInfoContent = null;
  #fixedInfoResizeObserver = null;
  #fixedInfoResizeCleanup = null;
  #fixedInfoScrollRafId = null;
  #fixedInfoLastScrollY = 0;
  #fixedInfoScrollDirection = 'down';
  #fixedInfoPreviousScrollDirection = 'down';

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#options = JSON.parse(this.getAttribute('data-section-settings') || '{}');
    this.#delegate = new Delegate(this);

    const swiperInitEl = this.querySelector('swiper-init.main-product__swiper');
    if (this.#options.slides_number > 1 && swiperInitEl) {
      if (swiperInitEl.swiper) {
        this.#onSwiperReady({ detail: { swiper: swiperInitEl.swiper } });
      } else {
        swiperInitEl.addEventListener('swiper:initialized', this.#onSwiperReady.bind(this), {
          once: true,
        });
      }
    }

    this.#delegate.on('variant:changed', this.#onVariantChanged.bind(this));
    this.#initHeaderScrollHide();
    this.#initFixedInfoScroll();

    if (this.#options.enable_zoom) {
      this.#initZoom();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.#onHeaderScroll);
    if (this.#headerScrollRafId !== null) {
      cancelAnimationFrame(this.#headerScrollRafId);
      this.#headerScrollRafId = null;
    }
    this.#stickyHeaderWrap?.classList.remove('header--ScrollHidden');
    this.#stickyHeaderWrap = null;
    window.removeEventListener('scroll', this.#onFixedInfoScroll);
    if (this.#fixedInfoScrollRafId !== null) {
      cancelAnimationFrame(this.#fixedInfoScrollRafId);
      this.#fixedInfoScrollRafId = null;
    }
    this.#fixedInfoResizeObserver?.disconnect();
    this.#fixedInfoResizeObserver = null;
    this.#fixedInfoResizeCleanup?.removeResizeListener();
    this.#fixedInfoResizeCleanup = null;
    this.#resetFixedInfoScroll();
    this.#fixedInfoContainer = null;
    this.#fixedInfoSpacer = null;
    this.#fixedInfoContent = null;
    if (this.#zoomLoadListener) {
      window.removeEventListener('load', this.#zoomLoadListener);
      this.#zoomLoadListener = null;
    }
    this.#imageZoom?.destroy();
    this.#imageZoom = null;
    this.#delegate?.off();
    this.#initialized = false;
    this.#swiper = null;
  }

  #onSwiperReady(event) {
    this.#swiper = event.detail.swiper;
  }

  #onVariantChanged(event) {
    const imageId = event?.detail?.new_image || this.#options.variant_image_id;
    if (!imageId) return;

    const imageEl = this.querySelector(`#Image--${imageId}`);
    if (!imageEl) return;

    const slideElement = imageEl.closest('.swiper-slide');
    if (!slideElement) return;

    const slideIndex = Array.from(slideElement.parentNode.children).indexOf(slideElement);
    this.#swiper?.slideTo(slideIndex);
  }

  #initZoom() {
    const initZoom = () => {
      if (!this.isConnected) return;

      if (typeof window.RevolutionImageZoom === 'function') {
        this.#imageZoom = new window.RevolutionImageZoom(this, this.#swiper);
        this.#zoomLoadListener = null;
      } else {
        this.#zoomLoadListener = initZoom;
        window.addEventListener('load', initZoom, { once: true });
      }
    };
    initZoom();
  }

  #initHeaderScrollHide() {
    const header = document.getElementById('header');
    this.#stickyHeaderWrap = header?.closest('.header--Sticky') ?? header?.parentElement ?? null;
    if (!this.#stickyHeaderWrap) return;

    this.#lastScrollY = window.scrollY;
    document.addEventListener('scroll', this.#onHeaderScroll, { passive: true });
  }

  #initFixedInfoScroll() {
    this.#fixedInfoContainer = this.querySelector('[data-fixed-info-container]');
    this.#fixedInfoSpacer = this.querySelector('[data-fixed-info-spacer]');
    this.#fixedInfoContent = this.querySelector('[data-fixed-info-content]');

    if (!this.#fixedInfoContainer || !this.#fixedInfoSpacer || !this.#fixedInfoContent) return;

    this.#fixedInfoLastScrollY = window.scrollY;
    this.#fixedInfoScrollDirection = 'down';
    this.#fixedInfoPreviousScrollDirection = 'down';
    window.addEventListener('scroll', this.#onFixedInfoScroll, { passive: true });

    this.#fixedInfoResizeObserver = new ResizeObserver(this.#scheduleFixedInfoUpdate);
    this.#fixedInfoResizeObserver.observe(this.#fixedInfoContainer);
    this.#fixedInfoResizeObserver.observe(this.#fixedInfoContent);
    this.#fixedInfoResizeCleanup = responsive.addResizeListener(
      this,
      this.#scheduleFixedInfoUpdate
    );
    this.#scheduleFixedInfoUpdate();
  }

  #onFixedInfoScroll = () => {
    const scrollY = window.scrollY;

    if (scrollY === this.#fixedInfoLastScrollY) return;

    this.#fixedInfoPreviousScrollDirection = this.#fixedInfoScrollDirection;
    this.#fixedInfoScrollDirection = scrollY > this.#fixedInfoLastScrollY ? 'down' : 'up';
    this.#fixedInfoLastScrollY = scrollY;
    this.#scheduleFixedInfoUpdate();
  };

  #scheduleFixedInfoUpdate = () => {
    if (this.#fixedInfoScrollRafId !== null) return;

    this.#fixedInfoScrollRafId = requestAnimationFrame(() => {
      this.#fixedInfoScrollRafId = null;
      this.#updateFixedInfoScroll();
    });
  };

  #updateFixedInfoScroll() {
    if (
      !this.#fixedInfoContainer ||
      !this.#fixedInfoSpacer ||
      !this.#fixedInfoContent ||
      !responsive.matchesBreakpoint('lap_and_up')
    ) {
      this.#resetFixedInfoScroll();
      return;
    }

    const containerHeight = this.#fixedInfoContainer.offsetHeight;
    const contentHeight = this.#fixedInfoContent.offsetHeight;
    const isViewportTall = contentHeight > window.innerHeight;
    const hasStickyTravel = containerHeight > contentHeight;

    if (!isViewportTall || !hasStickyTravel) {
      this.#resetFixedInfoScroll();
      return;
    }

    const bottomAlignedTop = window.innerHeight - contentHeight;

    if (this.#fixedInfoPreviousScrollDirection !== this.#fixedInfoScrollDirection) {
      const containerTop = this.#fixedInfoContainer.getBoundingClientRect().top;
      const contentTop = this.#fixedInfoContent.getBoundingClientRect().top;
      const maxSpacer = containerHeight - contentHeight;
      const preservedOffset = Math.min(Math.max(contentTop - containerTop, 0), maxSpacer);

      this.#fixedInfoSpacer.style.marginTop = `${preservedOffset}px`;
    }

    if (this.#fixedInfoScrollDirection === 'down') {
      this.#fixedInfoContent.style.top = `${bottomAlignedTop}px`;
      this.#fixedInfoContent.style.removeProperty('bottom');
      return;
    }

    const headerHeight = this.#stickyHeaderWrap?.offsetHeight ?? 0;
    this.#fixedInfoContent.style.top = 'auto';
    this.#fixedInfoContent.style.bottom = `${bottomAlignedTop - headerHeight}px`;
  }

  #resetFixedInfoScroll() {
    this.#fixedInfoContent?.style.removeProperty('top');
    this.#fixedInfoContent?.style.removeProperty('bottom');
    this.#fixedInfoSpacer?.style.removeProperty('margin-top');
  }

  #onHeaderScroll = () => {
    if (!this.#stickyHeaderWrap || this.#headerScrollRafId !== null) return;

    this.#headerScrollRafId = requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      const delta = scrollY - this.#lastScrollY;

      if (scrollY <= 0) {
        this.#stickyHeaderWrap.classList.remove('header--ScrollHidden');
      } else if (delta > 4) {
        this.#stickyHeaderWrap.classList.add('header--ScrollHidden');
      } else if (delta < -4) {
        this.#stickyHeaderWrap.classList.remove('header--ScrollHidden');
      }

      this.#lastScrollY = scrollY;
      this.#headerScrollRafId = null;
    });
  };
}

if (!customElements.get('product-component')) {
  customElements.define('product-component', ProductComponent);
}
